import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'tv-favorites';

const readFavorites = (): string[] => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
  } catch {
    return [];
  }
};

export function useFavorites() {
  const [favorites, setFavorites] = useState<string[]>(() => readFavorites());

  useEffect(() => {
    const sync = () => setFavorites(readFavorites());
    window.addEventListener('storage', sync);
    window.addEventListener('tv-favorites-changed', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('tv-favorites-changed', sync);
    };
  }, []);

  const persist = useCallback((next: string[]) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // ignore write failures (private mode etc.)
    }
    window.dispatchEvent(new Event('tv-favorites-changed'));
    setFavorites(next);
  }, []);

  const toggleFavorite = useCallback(
    (id: string) => {
      const current = readFavorites();
      const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
      persist(next);
      return next.includes(id);
    },
    [persist],
  );

  const isFavorite = useCallback((id: string) => favorites.includes(id), [favorites]);

  return { favorites, toggleFavorite, isFavorite };
}